// The 17-agent registry for the Procureo System of Systems.
// Each agent declares its tier, capabilities and a `match` function used by the
// Command Palette to score natural-language input.

export const AGENTS = [
  // ─── Orchestrator (Strategic Partner Lead) ────────────────────────────
  { id: "agent-orch-01", tier: "ORCHESTRATOR", name: "Strategic Synthesizer",
    summary: "Synthesizes multi-tier outputs into executive verdicts.",
    keywords: ["synthesize", "summary", "executive", "strategy", "verdict", "brief"] },
  { id: "agent-orch-02", tier: "ORCHESTRATOR", name: "Action Recommender",
    summary: "Issues 30-day executable recommendations for category leads.",
    keywords: ["recommend", "action", "execute", "next step", "play", "negotiate"] },
  { id: "agent-orch-03", tier: "ORCHESTRATOR", name: "Decision-Adjusted Savings Auditor",
    summary: "Applies Strategic Discounting; audit-grade Decision Proof.",
    keywords: ["audit", "savings", "decision-adjusted", "decision proof", "discount", "cfo"] },
  { id: "agent-orch-04", tier: "ORCHESTRATOR", name: "Executive Briefing Composer",
    summary: "1-pager briefing across categories for leadership.",
    keywords: ["briefing", "executive", "report", "leadership"] },
  // ─── SME (Technical Agent) ──────────────────────────────────────────────
  { id: "agent-sme-01", tier: "SME", name: "Entity Resolution Agent",
    summary: "UEI / CAGE / DUNS matching across data sources.",
    keywords: ["entity", "uei", "duns", "cage", "match", "resolve"] },
  { id: "agent-sme-02", tier: "SME", name: "Filing Extraction Agent",
    summary: "Extracts risk flags from SEC 10-K Item 1A.",
    keywords: ["sec", "10-k", "filing", "going concern", "risk flag", "extract"] },
  { id: "agent-sme-03", tier: "SME", name: "UNSPSC Mapper",
    summary: "Maps spend descriptions to UNSPSC segment & family.",
    keywords: ["unspsc", "mapping", "classify", "segment", "family"] },
  { id: "agent-sme-04", tier: "SME", name: "Should-Cost Analyst",
    summary: "Decomposes spend into FAR/DCAA cost buckets.",
    keywords: ["should-cost", "should cost", "decomposition", "cost model", "far", "dcaa"] },
  { id: "agent-sme-05", tier: "SME", name: "Rate Benchmark Analyst",
    summary: "Benchmarks unit rates against GSA/CALC/FPDS sources.",
    keywords: ["rate", "benchmark", "gsa", "fpds", "median", "percentile"] },
  { id: "agent-sme-06", tier: "SME", name: "Counterparty PDI Scorer",
    summary: "Composite 0-100 PDI score (100 = max integrity).",
    keywords: ["pdi", "score", "counterparty", "tier", "sentinel", "integrity"] },
  { id: "agent-sme-07", tier: "SME", name: "Supplier Concentration Analyst",
    summary: "Detects single-source / over-concentration exposure.",
    keywords: ["concentration", "exposure", "single source", "spend share"] },
  // ─── Utility (System Processor) ─────────────────────────────────────────────
  { id: "agent-util-01", tier: "UTILITY", name: "Vendor Name Normalizer",
    summary: "Strips legal suffixes, casing hygiene.",
    keywords: ["normalize", "name", "vendor", "supplier", "clean"] },
  { id: "agent-util-02", tier: "UTILITY", name: "Date Formatter",
    summary: "ISO-8601 coercion across SAM/SAP timestamps.",
    keywords: ["date", "format", "timestamp", "iso"] },
  { id: "agent-util-03", tier: "UTILITY", name: "Currency Hygiene Agent",
    summary: "USD normalization, FX touch-up.",
    keywords: ["currency", "usd", "fx", "money", "format"] },
  { id: "agent-util-04", tier: "UTILITY", name: "Address Hygiene Agent",
    summary: "USPS / DUNS address parsing & dedup.",
    keywords: ["address", "geocode", "usps", "dedup"] },
  // ─── System ──────────────────────────────────────────────────────────────
  { id: "agent-sys-01", tier: "SYSTEM", name: "Spend Importer Agent",
    summary: "100K-row SAP/Coupa stress-pipeline (Phase 2 P2-01).",
    keywords: ["import", "ingest", "sap", "coupa", "stress"] },
  { id: "agent-sys-02", tier: "SYSTEM", name: "Integrity Assertion Runner",
    summary: "Runs the 11 SQL assertions powering the CFO badge.",
    keywords: ["integrity", "assertion", "cfo", "badge", "verify"] },
];

// ─── Navigation targets exposed via the palette ─────────────────────────────
export const TAB_TARGETS = [
  { id: "tab-strategy",     label: "Strategy Command",     tab: "strategy",     keywords: ["strategy", "command", "treemap", "spend", "ledger", "kpi"] },
  { id: "tab-opportunities", label: "Opportunities & Funnel", tab: "opportunities", keywords: ["opportunity", "opportunities", "funnel", "ooda", "pipeline"] },
  { id: "tab-sentinel",     label: "Sentinel · Risk",      tab: "sentinel",     keywords: ["sentinel", "risk", "supplier", "going concern"] },
  { id: "tab-learning",     label: "System Learning",      tab: "learning",     keywords: ["learning", "context ledger", "trace", "logs", "memory"] },
];

// ─── Verb-shaped actions invoked from the palette ───────────────────────────
// Each returns a Promise resolving with the data the palette should display
// (or returns the navigation target).
export function buildActions({ api, navigate, openProof, openSourcing }) {
  return [
    {
      id: "act-should-cost",
      label: "Run Should-Cost for PO",
      hint: "should-cost PO-XXXXX-XXX",
      keywords: ["should-cost", "should cost", "run should"],
      pattern: /should[\s-]?cost(?:\s+for)?\s+(PO[-\w]+)/i,
      async run(match) {
        const poNum = match?.[1];
        const ledger = await api.ledger({ limit: 200 });
        const row = poNum && ledger.find(r => r.po_number.toLowerCase() === poNum.toLowerCase());
        if (!row) return { error: `PO not found in ledger. Try a number like PO-00103-VA.` };
        navigate("strategy");
        openSourcing("SHOULD_COST", row);
        return { ok: `Routed to Strategy · Should-Cost for ${row.po_number} (${row.supplier})` };
      },
    },
    {
      id: "act-rate-benchmark",
      label: "Run Rate Benchmark for PO",
      hint: "rate benchmark PO-XXXXX-XXX",
      keywords: ["rate benchmark", "rate", "benchmark"],
      pattern: /rate[\s-]?benchmark(?:\s+for)?\s+(PO[-\w]+)/i,
      async run(match) {
        const poNum = match?.[1];
        const ledger = await api.ledger({ limit: 200 });
        const row = poNum && ledger.find(r => r.po_number.toLowerCase() === poNum.toLowerCase());
        if (!row) return { error: `PO not found. Try a number like PO-00103-VA.` };
        navigate("strategy");
        openSourcing("RATE_BENCHMARK", row);
        return { ok: `Routed to Strategy · Rate Benchmark for ${row.po_number}` };
      },
    },
    {
      id: "act-show-risk",
      label: "Show Risk for Supplier",
      hint: "risk for [supplier name]",
      keywords: ["risk for", "show risk", "supplier risk"],
      pattern: /(?:risk(?:\s+score)?(?:\s+for)?|show\s+risk(?:\s+for)?)\s+(.+)/i,
      async run(match) {
        const q = (match?.[1] || "").trim().toLowerCase();
        if (!q) return { error: "Please include a supplier name." };
        const sentinel = await api.riskSentinel(50);
        const row = sentinel.suppliers.find(s => s.supplier.toLowerCase().includes(q));
        if (!row) return { error: `No supplier matched "${match[1]}".` };
        navigate("sentinel");
        openProof("RISK", row);
        return { ok: `Routed to Sentinel · ${row.supplier} (score ${row.risk_score} ${row.risk_tier})` };
      },
    },
    {
      id: "act-find-po",
      label: "Find PO and open Proof",
      hint: "find PO-XXXXX-XXX",
      keywords: ["find po", "open po", "show po"],
      pattern: /(?:find|open|show)\s+(PO[-\w]+)/i,
      async run(match) {
        const poNum = match?.[1];
        const ledger = await api.ledger({ limit: 200 });
        const row = ledger.find(r => r.po_number.toLowerCase() === poNum.toLowerCase());
        if (!row) return { error: `PO not found.` };
        navigate("strategy");
        openProof("SAVINGS", row);
        return { ok: `Routed to Strategy · Proof for ${row.po_number} (${row.supplier})` };
      },
    },
    {
      id: "act-stage-funnel",
      label: "Show Funnel Stage (OBSERVE / ORIENT / DECIDE / ACT)",
      hint: "show stage [observe|orient|decide|act]",
      keywords: ["stage", "funnel", "ooda"],
      pattern: /\b(observe|orient|decide|act)\b/i,
      async run(match) {
        navigate("opportunities");
        return { ok: `Routed to Opportunities — focusing on ${match[1].toUpperCase()} stage.`, stage: match[1].toUpperCase() };
      },
    },
  ];
}

// Generic scorer for free-text input.
export function scoreItem(text, item) {
  if (!text) return 0;
  const t = text.toLowerCase();
  const haystack = [
    item.name || item.label || "",
    item.summary || item.hint || "",
    (item.keywords || []).join(" "),
  ].join(" ").toLowerCase();
  let score = 0;
  if (haystack.includes(t)) score += 10;
  for (const w of t.split(/\s+/).filter(Boolean)) {
    if (haystack.includes(w)) score += 3;
  }
  return score;
}
